import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState, useId } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { Button, FormControl, Heading, HStack, Modal, Text, TextArea, VStack } from 'native-base'
import { Controller, useForm } from 'react-hook-form'
import { Platform } from 'react-native'
import * as Yup from 'yup'

import { OpportunityDeletionAttachmentsField } from './OpportunityDeletionAttachmentsField'
import { OpportunityDeletionJustificationSelect } from './OpportunityDeletionJustificationSelect'
import {
  OpportunityDeletionAttachment,
  OpportunityDeletionJustificationOption,
  OpportunityDeletionModalProps,
  OpportunityDeletionSubmitData,
  OPPORTUNITY_DELETION_OTHER_VALUE,
  OPPORTUNITY_DELETION_JUSTIFICATION_OPTIONS
} from './types'

type OpportunityDeletionFormData = {
  reason: OpportunityDeletionJustificationOption | ''
  description: string
  attachments: OpportunityDeletionAttachment[]
}

const JUSTIFICATION_OPTIONS: OpportunityDeletionJustificationOption[] = [
  ...OPPORTUNITY_DELETION_JUSTIFICATION_OPTIONS
]

const JUSTIFICATION_SELECT_OPTIONS = JUSTIFICATION_OPTIONS.map((option) => ({
  label: option,
  value: option
}))

const schema: Yup.SchemaOf<OpportunityDeletionFormData> = Yup.object({
  reason: Yup.mixed<OpportunityDeletionJustificationOption>()
    .oneOf(JUSTIFICATION_OPTIONS, '*Campo obrigatório.')
    .required('*Campo obrigatório.'),
  description: Yup.string()
    .transform((value) => value?.trim() ?? '')
    .max(300, 'O campo pode ter no máximo 300 caracteres')
    .when('reason', {
      is: (value: OpportunityDeletionJustificationOption | '') =>
        value === OPPORTUNITY_DELETION_OTHER_VALUE,
      then: (schema) => schema.required('*Campo obrigatório.'),
      otherwise: (schema) => schema.optional()
    }),
  attachments: Yup.array()
    .of(
      Yup.object({
        file_name: Yup.string().required(),
        storage_file_name: Yup.string().required(),
        url: Yup.string().required(),
        created_at: Yup.string().required(),
        size: Yup.number().required(),
        extension: Yup.string().required()
      })
    )
    .default([])
}) as Yup.SchemaOf<OpportunityDeletionFormData>

const defaultValues: OpportunityDeletionFormData = {
  reason: '',
  description: '',
  attachments: []
}

export const OpportunityDeletionModal: FunctionComponent<OpportunityDeletionModalProps> = ({
  isOpen,
  opportunityName,
  onClose,
  onSubmit,
  isSubmitting
}) => {
  const descriptionId = useId()

  const headingRef = useRef<any>(null)

  const [attachmentsError, setAttachmentsError] = useState<string | null>(null)

  const [isUploadingAttachments, setIsUploadingAttachments] = useState(false)

  const { handleSubmit, watch, reset, formState, control, setValue, clearErrors } =
    useForm<OpportunityDeletionFormData>({
      resolver: yupResolver(schema),
      defaultValues,
      mode: 'onChange'
    })

  const reason = watch('reason')

  const description = watch('description') ?? ''

  const descriptionLength = description.length

  const focusHeading = useCallback(() => {
    setTimeout(() => headingRef.current?.focus?.(), 0)
  }, [])

  useEffect(() => {
    if (isOpen) {
      focusHeading()
    } else {
      reset(defaultValues)

      setAttachmentsError(null)

      setIsUploadingAttachments(false)
    }
  }, [focusHeading, isOpen, reset])

  useEffect(() => {
    if (reason !== OPPORTUNITY_DELETION_OTHER_VALUE && description) {
      setValue('description', '', { shouldValidate: true, shouldDirty: false })

      clearErrors('description')
    }
  }, [clearErrors, description, reason, setValue])

  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  const submit = useMemo(
    () =>
      handleSubmit(async (data) => {
        const payload: OpportunityDeletionSubmitData = {
          reason: data.reason as OpportunityDeletionJustificationOption,
          description:
            data.reason === OPPORTUNITY_DELETION_OTHER_VALUE
              ? data.description?.trim() ?? ''
              : undefined,
          attachments: data.attachments
        }

        await onSubmit(payload)
      }),
    [handleSubmit, onSubmit]
  )

  const isSubmitDisabled =
    !formState.isValid || isSubmitting || !!attachmentsError || isUploadingAttachments

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg" initialFocusRef={headingRef}>
      <Modal.Content
        bgColor="white"
        maxW="656px"
        borderRadius="32px"
        pt={10}
        pb={8}
        aria-describedby={descriptionId}
        style={{
          overflow: 'visible'
        }}>
        <Modal.Body
          p={0}
          px={8}
          pb={8}
          style={{
            overflow: 'visible'
          }}>
          <VStack space={8}>
            <VStack space={8}>
              <Heading
                ref={headingRef}
                {...(Platform.OS === 'web' ? { tabIndex: -1 } : {})}
                fontSize="22px"
                fontWeight={700}
                lineHeight="28px"
                color="gray.700"
                textAlign="center">
                Você tem certeza que deseja excluir a {opportunityName ?? 'oportunidade'} das suas
                oportunidades?
              </Heading>
              <Text
                id={descriptionId}
                fontSize="16px"
                fontWeight={400}
                lineHeight="24px"
                color="gray.500"
                textAlign="center">
                Ao confirmar, você solicitará a remoção dessa oportunidade da sua lista. Um de
                nossos analistas irá revisar a solicitação. Isso pode levar até 72 horas.
              </Text>
            </VStack>

            <VStack space={6}>
              <FormControl isRequired isInvalid={!!formState.errors.reason}>
                <FormControl.Label>
                  <Text fontSize="16px" lineHeight="24px" fontWeight={500} color="gray.500">
                    Justificativa
                  </Text>
                </FormControl.Label>

                <Controller
                  name="reason"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <OpportunityDeletionJustificationSelect
                      value={value || undefined}
                      onChange={(optionValue) =>
                        onChange((optionValue ?? '') as OpportunityDeletionJustificationOption | '')
                      }
                      options={JUSTIFICATION_SELECT_OPTIONS}
                    />
                  )}
                />

                <FormControl.ErrorMessage>
                  {formState.errors.reason?.message}
                </FormControl.ErrorMessage>
              </FormControl>

              {reason === OPPORTUNITY_DELETION_OTHER_VALUE && (
                <FormControl isRequired isInvalid={!!formState.errors.description}>
                  <Controller
                    name="description"
                    control={control}
                    render={({ field: { value, onChange, onBlur } }) => (
                      <TextArea
                        value={value}
                        onChangeText={onChange}
                        onBlur={onBlur}
                        placeholder="Por que você está solicitando a exclusão?"
                        borderRadius="24px"
                        bg="gray.30"
                        borderColor="gray.100"
                        minH="120px"
                        px={6}
                        py={4}
                        _focus={{ borderColor: 'gray.200', bg: 'white' }}
                        _hover={{ borderColor: 'gray.200' }}
                        autoCompleteType={undefined}
                        maxLength={300}
                        textAlignVertical="top"
                        color="gray.700"
                      />
                    )}
                  />
                  <HStack justifyContent="space-between" alignItems="center" mt={1}>
                    <FormControl.ErrorMessage>
                      {formState.errors.description?.message}
                    </FormControl.ErrorMessage>
                    <Text fontSize="12px" fontWeight={400} lineHeight="16px" color="gray.400">
                      {descriptionLength}/300
                    </Text>
                  </HStack>
                </FormControl>
              )}

              <FormControl isInvalid={!!attachmentsError}>
                <Controller
                  name="attachments"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <OpportunityDeletionAttachmentsField
                      attachments={value ?? []}
                      onChange={(items) => onChange(items)}
                      error={attachmentsError}
                      onError={setAttachmentsError}
                      onUploadStatusChange={setIsUploadingAttachments}
                    />
                  )}
                />
              </FormControl>
            </VStack>
          </VStack>
        </Modal.Body>

        <Modal.Footer px={8} borderWidth={0} borderColor="transparent" bgColor="white">
          <HStack space={4} width="100%">
            <Button flex={1} variant="outline" colorScheme="gray" onPress={handleClose}>
              Cancelar
            </Button>
            <Button
              flex={1}
              colorScheme="gray"
              onPress={submit}
              isDisabled={isSubmitDisabled}
              isLoading={isSubmitting}>
              Solicitar exclusão
            </Button>
          </HStack>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  )
}
